import React, {useState} from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Divider,
    Alert,
    CircularProgress,
    Link as MuiLink,
} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useAuth} from '../contexts/AuthContext';
import GoogleIcon from '@mui/icons-material/Google';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import {useGoogleLogin} from "@react-oauth/google";
import {useTranslation} from 'react-i18next';

const schema = yup.object({
    email: yup.string().email('Email non valida').required('Email richiesta'),
    password: yup.string().min(6, 'Password deve essere di almeno 6 caratteri').required('Password richiesta'),
});

type FormData = yup.InferType<typeof schema>;

const LoginPage: React.FC = () => {
    const {login, googleLogin, linkedinLogin} = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm<FormData>({
        resolver: yupResolver(schema),
    });

    const handleGoogleSuccess = async (tokenResponse: any) => {
        console.log('✅ Token ricevuto, recupero info utente...');
        try {
            setIsLoading(true);
            setError('');
            const userInfoRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
            });
            const userInfo = await userInfoRes.json();
            await googleLogin({ userInfo });
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore durante il login');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (data: FormData) => {
        try {
            setIsLoading(true);
            setError('');
            await login(data.email, data.password);
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore durante il login');
        } finally {
            setIsLoading(false);
        }
    };


    const handleLinkedInLogin = () => {
        linkedinLogin();
    };

    const googleLoginHook = useGoogleLogin({
        onSuccess: handleGoogleSuccess,
        onError: () => {
            console.error('Login fallito');
            setError('Login con Google fallito');
        },
        flow: 'implicit',
    });

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{padding: 4, width: '100%'}}>
                    <Typography component="h1" variant="h4" align="center" gutterBottom>
                        Accedi a CV Portal
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{mb: 2}}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{mt: 1}}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email"
                            autoComplete="email"
                            autoFocus
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            {...register('password')}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{mt: 3, mb: 2}}
                            disabled={isLoading}
                        >
                            {isLoading ? <CircularProgress size={24}/> : 'Accedi'}
                        </Button>

                        <Divider sx={{my: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                oppure
                            </Typography>
                        </Divider>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => googleLoginHook()}
                            startIcon={<GoogleIcon/>}
                            sx={{
                                py: 1.5,
                                bgcolor: '#4285f4',
                                color: '#fff',
                                '&:hover': {
                                    bgcolor: '#3367d6',
                                },
                                fontSize: '1rem',
                                fontWeight: 500,
                                textTransform: 'none',
                            }}
                        >
                            Accedi con Google
                        </Button>

                        {/*<Button*/}
                        {/*    fullWidth*/}
                        {/*    variant="outlined"*/}
                        {/*    onClick={handleLinkedInLogin}*/}
                        {/*    startIcon={<LinkedInIcon/>}*/}
                        {/*    sx={{mb: 2}}*/}
                        {/*>*/}
                        {/*    Accedi con LinkedIn*/}
                        {/*</Button>*/}

                        <Box textAlign="center">
                            <Typography variant="body2">
                                <MuiLink component={Link} to="/forgot-password">
                                    {t('login.forgotPassword')}
                                </MuiLink>
                            </Typography>
                        </Box>
                        <Box textAlign="center" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                                Non hai un account?{' '}
                                <MuiLink component={Link} to="/register">
                                    Registrati
                                </MuiLink>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default LoginPage;
